import axios, { AxiosInstance, HttpStatusCode } from 'axios';
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { ROUTER_URL } from '../../router/constants';

// Constants for storage keys
export const HTTP_STATUS = 'httpStatus'; // Key for storing HTTP status in storage events
export const TOKEN_KEY = 'RECTANGLE_TOKEN'; // Key for storing authentication token

/**
 * Custom hook for creating an Axios instance with authentication and error handling
 * - Automatically adds Authorization header
 * - Handles token expiration and unauthorized responses
 * - Manages redirects to login when needed
 *
 * @returns {React.MutableRefObject<AxiosInstance | undefined>} Reference to the Axios instance
 */
export const useAxios = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const axiosInstance = useRef<AxiosInstance>();
  const token = localStorage.getItem(TOKEN_KEY);

  useEffect(() => {
    // Create Axios instance with default configuration
    axiosInstance.current = axios.create({
      withCredentials: true, // Send cookies with requests
      headers: {
        Authorization: token ? `Bearer ${token}` : '', // Add Bearer token if available
      },
    });

    // Add response interceptor for error handling
    axiosInstance.current?.interceptors.response.use(
      (response) => response, // Pass through successful responses
      (error) => {
        const { status, data } = (error.response || {}) as {
          status?: number;
          data: { error: any };
        };

        // Extract error message from response
        const message = typeof data === 'string' ? data : data?.error?.message;

        // List of error messages that should not trigger storage events
        const loginErrorMessage = [
          'Password_is_incorrect',
          'Username_does_not_exist',
          'Your_account_is_lock',
        ];

        // Handle specific error cases
        if (
          status === HttpStatusCode.Unauthorized &&
          message === 'Token_not_found'
        ) {
          // Clear token and redirect to login for missing token
          localStorage.removeItem(TOKEN_KEY);
          return navigate(ROUTER_URL.Login, { replace: true });
        } else if (
          status === HttpStatusCode.Unauthorized &&
          !loginErrorMessage.includes(message) &&
          location.pathname !== ROUTER_URL.Login
        ) {
          // Dispatch storage event for other unauthorized errors (except login-related)
          return window.dispatchEvent(
            new StorageEvent('storage', {
              key: HTTP_STATUS,
              newValue: message,
            })
          );
        }

        // Re-throw other errors for component-level handling
        throw error;
      }
    );

    // Cleanup function to clear Axios instance
    return () => {
      axiosInstance.current = undefined;
    };
  }, [token]);

  return axiosInstance;
};
